import moment, { Moment } from "moment";

import { cropIsWithinReportingYear } from "utils";

import { CROP_TYPE_FCPO, CarbonProgram, CropFCPO, PracticeCrop } from "models";

import { SOIL_PLUS_3_START_YEAR } from "pages/app/field-timeline/utils/PayoutUtils";

// Exception for until soil plus 3 start year: until the farm has submitted data for that year, practice data should not be locked
export const getHasSubmissionForSoilPlus3StartYear = (lastSubmissionYear: number | null | undefined): boolean =>
  !!lastSubmissionYear && lastSubmissionYear >= SOIL_PLUS_3_START_YEAR;

// SoilPlus 3.0 data-gap values are exempt from locking only until the farm submits: once submitted they lock like any other data
export const getIsSoilPlus3DataCollectionEditable = (
  isSoilPlus3DataCollection: boolean | null | undefined,
  lastSubmissionYear: number | null | undefined
): boolean => !!isSoilPlus3DataCollection && !getHasSubmissionForSoilPlus3StartYear(lastSubmissionYear);

export const getFirstUnlockedYear = (activeFarmReportingYear: number, isReportingYearLocked: boolean): number =>
  isReportingYearLocked ? activeFarmReportingYear + 1 : activeFarmReportingYear;

export const getFirstEditableYear = (activeFarmReportingYear: number | undefined, lastSubmissionYear: number | null | undefined): number | undefined =>
  activeFarmReportingYear === undefined ? undefined : getFirstUnlockedYear(activeFarmReportingYear, lastSubmissionYear === activeFarmReportingYear);

// This function checks if the year is included in the baseline years or in the years between the baseline and the reporting year
export const getIsBaselineOrHistoricYear = (baselineYears: number[] | undefined, year: number, reportingYear: number | undefined): boolean => {
  if (!reportingYear) return false;
  if (!baselineYears) {
    return year !== reportingYear;
  }

  return baselineYears.includes(year) || (year > Math.max(...baselineYears) && year < reportingYear);
};

// whether a crop's own seeding/harvest date fields should be locked — confirmed, outside the current reporting
// year, and either past the farm's last submission or old enough (more than a year behind the reporting year)
export const getCropDateEditDisabled = ({
  crop,
  carbonProgram,
  activeFarmReportingYear,
  lastSubmissionDate,
  lastSubmissionYear,
  isAdmin,
}: {
  crop: CropFCPO;
  carbonProgram: CarbonProgram | null | undefined;
  activeFarmReportingYear: number | undefined;
  lastSubmissionDate: string | null | undefined;
  lastSubmissionYear: number | null | undefined;
  isAdmin: boolean;
}): {
  isCropSeedingDateEditDisabled: boolean;
  isCropHarvestDateEditDisabled: boolean;
  hasSubmissionForSoilPlus3StartYear: boolean;
  isCropNameEditDisabled: boolean;
  cropIsWithinCurrentReportingYear: boolean;
} => {
  const firstEditableYear = getFirstEditableYear(activeFarmReportingYear, lastSubmissionYear);
  const cropIsWithinCurrentReportingYear = cropIsWithinReportingYear({
    carbonProgram,
    cropType: crop.cropType,
    harvestDate: crop.harvestEndDate,
    reportingYear: firstEditableYear,
    seedingDate: crop.seedingStartDate,
  });

  const hasSubmissionForSoilPlus3StartYear = getHasSubmissionForSoilPlus3StartYear(lastSubmissionYear);

  // a confirmed crop still within the current reporting year can have its crop name changed — verification
  // doesn't fully freeze it until the year closes
  const isCropNameEditDisabled = !!crop.isConfirmed && !cropIsWithinCurrentReportingYear && !isAdmin;

  const isCropSeedingDateEditDisabled =
    !isAdmin &&
    !!crop.seedingStartDate &&
    !!crop.isConfirmed &&
    !cropIsWithinCurrentReportingYear &&
    hasSubmissionForSoilPlus3StartYear &&
    ((!!lastSubmissionDate && moment(crop.seedingStartDate).isBefore(lastSubmissionDate)) ||
      (!!firstEditableYear && !!crop.year && crop.year < firstEditableYear - 1));

  const isCropHarvestDateEditDisabled =
    !isAdmin &&
    !!crop.harvestEndDate &&
    !!crop.isConfirmed &&
    !cropIsWithinCurrentReportingYear &&
    hasSubmissionForSoilPlus3StartYear &&
    ((!!lastSubmissionDate && moment(crop.harvestEndDate).isBefore(lastSubmissionDate)) ||
      (!!firstEditableYear && !!crop.year && crop.year < firstEditableYear - 1));

  return {
    isCropSeedingDateEditDisabled,
    isCropHarvestDateEditDisabled,
    isCropNameEditDisabled,
    cropIsWithinCurrentReportingYear,
    hasSubmissionForSoilPlus3StartYear,
  };
};

// whether a practice's inputs (e.g. seeding, tillage) should be locked — a baseline/historic year for that
// practice with already-confirmed bundle data; a soilPlus3-collection practice is exempt until the farm submits
export const getPracticeInputsDisabled = ({
  isPartOfSoilPlus3DataCollection,
  baselineYears,
  cropYear,
  activeFarmReportingYear,
  lastSubmissionYear,
  bundleHasConfirmedData,
  isAdmin,
}: {
  isPartOfSoilPlus3DataCollection: boolean;
  baselineYears: number[] | undefined;
  cropYear: number;
  activeFarmReportingYear: number | undefined;
  isDataSubmitted: boolean;
  lastSubmissionYear: number | null | undefined;
  bundleHasConfirmedData: boolean | undefined;
  isAdmin: boolean;
}): boolean =>
  getIsSoilPlus3DataCollectionEditable(isPartOfSoilPlus3DataCollection, lastSubmissionYear)
    ? false
    : getIsBaselineOrHistoricYear(baselineYears, cropYear, getFirstEditableYear(activeFarmReportingYear, lastSubmissionYear)) &&
      !!bundleHasConfirmedData &&
      !isAdmin;

// the yield locks together with the crop's dates, unless it is still required and missing, or open SoilPlus 3.0 data collection
export const getIsYieldEditDisabled = ({
  crop,
  isYieldRequired,
  isCropSeedingDateEditDisabled,
  isCropHarvestDateEditDisabled,
  lastSubmissionYear,
}: {
  crop: CropFCPO;
  isYieldRequired: boolean;
  isCropSeedingDateEditDisabled: boolean;
  isCropHarvestDateEditDisabled: boolean;
  lastSubmissionYear: number | null | undefined;
}): boolean => {
  if (isYieldRequired && (!crop.yieldPerHa || crop.yieldPerHa <= 0)) return false;
  if (isYieldRequired && getIsSoilPlus3DataCollectionEditable(crop.yieldSoilPlus3DataCollection, lastSubmissionYear)) return false;
  return isCropSeedingDateEditDisabled && isCropHarvestDateEditDisabled;
};

export const getCropRecommendedDates = ({
  crop,
  cropType,
  selectedYear,
}: {
  crop: PracticeCrop;
  cropType: CROP_TYPE_FCPO;
  selectedYear?: number;
}): { recommendedSeedingDate: Moment; recommendedHarvestDate: Moment } | null => {
  if (cropType === CROP_TYPE_FCPO.UNDERSOWING) {
    return null;
  }

  const yearForRecommendation = selectedYear ?? moment().year();
  const recommendedSeedingDayAndMonth = crop.sequestrationValues?.[0]?.seedingStart;
  const recommendedHarvestDayAndMonth = crop.sequestrationValues?.[0]?.harvestStart;

  const seedingStartDate = moment(`${recommendedSeedingDayAndMonth}/${yearForRecommendation}`, "DD/MM/YYYY");
  const harvestEndDate = moment(`${recommendedHarvestDayAndMonth}/${yearForRecommendation}`, "DD/MM/YYYY");

  if (!seedingStartDate.isValid() || !harvestEndDate.isValid()) {
    return null;
  }

  if (harvestEndDate.isBefore(seedingStartDate)) {
    if (cropType === CROP_TYPE_FCPO.MAIN) {
      seedingStartDate.subtract(1, "year");
    }
    if (cropType === CROP_TYPE_FCPO.COVER) {
      harvestEndDate.add(1, "year");
    }
  }
  return { recommendedSeedingDate: seedingStartDate, recommendedHarvestDate: harvestEndDate };
};
